import { ReadObject } from './storage'
import { buildSessionExpiredLoginUrl, clearExpiredSessionData, captureSessionState, isSessionStateCurrent } from './session'
import { isLitePath, LITE_BASE_PATH } from 'utils/portal'

export const DEV_MODE = false;

const ENDPOINTS = {
	// 'localhost': 'http://13.60.115.211:1337/api',
	'localhost': 'http://localhost:1337/api',
	// 'localhost': 'https://admin.magventureuniverse.com/api',
	'16.16.62.165': 'http://13.49.185.253:1337/api',

	'13.60.115.211': 'http://13.60.115.211:1337/api',

	'magventureuniverse.com': 'https://admin.magventureuniverse.com/api',
	'staging-xapps.dev': 'https://magventure-api.staging-xapps.dev/api'
}

const BUCKET_ENDPOINTS = {
	'localhost': 'http://localhost:1337',
	'staging-xapps.dev': '{/* #STRAPI_ENDPOINT */}',
}

const SOCKET_ENDPOINTS = {
	'localhost': 'http://localhost:1337',
	'staging-xapps.dev': '{/* #STRAPI_ENDPOINT */}',
}

const envEndpoint = (ends) => {
	return (
		Object.keys(ends)
			.filter(fit => `${window.location.origin}`.indexOf(fit) !== -1)
			.map(key => ends[key])[0]
	) || ends['magventureuniverse.com']
}

const configuredApiEndpoint = String(process.env.REACT_APP_API_ENDPOINT || '').trim().replace(/\/$/, '')

export const API_ENDPOINT = configuredApiEndpoint || envEndpoint(ENDPOINTS)
export const BUCKET_ENDPOINT = process.env.REACT_APP_BUCKET_ENDPOINT || envEndpoint(BUCKET_ENDPOINTS)
export const SOCKET_ENDPOINT = process.env.REACT_APP_SOCKET_ENDPOINT || envEndpoint(SOCKET_ENDPOINTS)

const resolveActiveSession = async () => {
	const authentication = await ReadObject('authentication')
	const mvuEssentials = await ReadObject('mvu_essentials')
	const mvuAccelerator = await ReadObject('mvu_accelerator')
	const pathname = window?.location?.pathname || ''
	// Entry screens have no area context yet. Use an existing scoped token;
	// area content routes below still require their own token without fallback.
	if (['/', '/login', '/choose-area'].includes(pathname)) {
		for (const [session, sessionKey] of [[authentication, 'authentication'], [mvuEssentials, 'mvu_essentials'], [mvuAccelerator, 'mvu_accelerator']]) {
			if (session?.jwt) return { session, sessionKey, loginPath: '/login' }
		}
	}
	const isMVUEssentialsArea = isLitePath(pathname)
	const isMVUAcceleratorArea = pathname.startsWith('/mvu-accelerator')

	if (isMVUEssentialsArea) {
		return mvuEssentials?.jwt
			? { session: mvuEssentials, sessionKey: 'mvu_essentials', loginPath: `${LITE_BASE_PATH}/login` }
			: { session: {}, sessionKey: 'mvu_essentials', loginPath: `${LITE_BASE_PATH}/login` }
	}

	if (isMVUAcceleratorArea) {
		return mvuAccelerator?.jwt
			? { session: mvuAccelerator, sessionKey: 'mvu_accelerator', loginPath: '/mvu-accelerator/login' }
			: { session: {}, sessionKey: 'mvu_accelerator', loginPath: '/mvu-accelerator/login' }
	}

	return { session: authentication, sessionKey: 'authentication', loginPath: '/login' }
}

export const downloadUrl = () => {
	return `${API_ENDPOINT}/s3-download`;
}

export const GetHeaders = async authenticated => {
	const headers = { 'Content-Type': 'application/json' }
	if (authenticated) headers['X-Magventure-Area'] = isLitePath(window.location.pathname) ? 'lite'
		: window.location.pathname.startsWith('/mvu-accelerator') ? 'accelerator' : 'universe'
	const { session: authSession } = await resolveActiveSession()

	if (authenticated && ['/', '/login', '/choose-area'].includes(window.location.pathname) && authSession?.identity?.area) {
		headers['X-Magventure-Area'] = authSession.identity.area
	}
	if (authenticated && authSession?.jwt) {
		headers.Authorization = `Bearer ${authSession.jwt}`
	}
	return { headers }
}

const sessionValidations = new Map()
let isRedirectingToLogin = false

const isMeRequest = url => {
	try {
		return new URL(url, window.location.origin).pathname === new URL(`${API_ENDPOINT}/me`).pathname
	} catch (error) {
		return false
	}
}

const validateCurrentSession = headers => {
	const sessionPath = ReadObject('identity_session')?.centralUserId ? '/identity/session' : '/me'
	const key = `${headers.Authorization || ''}:${headers['X-Magventure-Area'] || ''}:${sessionPath}`
	if (!sessionValidations.has(key)) {
		const pending = fetch(`${API_ENDPOINT}${sessionPath}`, { method: 'GET', headers })
			.then(response => response.status !== 401)
			.catch(() => true)
			.finally(() => { sessionValidations.delete(key) })
		sessionValidations.set(key, pending)
	}
	return sessionValidations.get(key)
}

const handleUnauthorized = async (url, headers, snapshot) => {
	if (!isSessionStateCurrent(snapshot)) return
	const managed = Boolean(ReadObject('identity_session')?.centralUserId)
	const sessionIsValid = isMeRequest(url) && !managed
		? false
		: await validateCurrentSession(headers)

	if (sessionIsValid || isRedirectingToLogin || !isSessionStateCurrent(snapshot)) return

	const { sessionKey, loginPath } = await resolveActiveSession()
	if (!isSessionStateCurrent(snapshot) || isRedirectingToLogin) return
	isRedirectingToLogin = true
	await clearExpiredSessionData(sessionKey)

	if (window?.location?.pathname !== loginPath) {
		window.location.replace(buildSessionExpiredLoginUrl(loginPath))
	}

	setTimeout(() => {
		isRedirectingToLogin = false
	}, 1500)
}

export const ServerFetch = async (url, options, authenticated) => {
	const snapshot = captureSessionState()
	const { headers } = await GetHeaders(authenticated)
	// console.info(url, options, headers)
	try {
		const response = await fetch(url, { ...options, headers })
		if (response.status === 401 && authenticated) {
			await handleUnauthorized(url, headers, snapshot)
		}
		try {
			return await response.json()
		} catch (err) {
			console.log('ServerParseError', err)
			return { error: true, message: response }
		}
	} catch (error) {
		console.log('ServerFetchError', error)
		return false;
	}
}

// Crud

export const GET = async (path, authenticated = false, requestOptions = {}) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			...requestOptions,
			method: 'GET'
		},
		authenticated
	)
}

export const POST = async (path, body, authenticated = false, requestOptions = {}) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			...requestOptions,
			method: 'POST',
			body: JSON.stringify(body)
		},
		authenticated
	)
}

export const PUT = async (path, body, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'PUT',
			body: JSON.stringify(body)
		},
		authenticated
	)
}

export const PATCH = async (path, body, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'PATCH',
			body: JSON.stringify(body)
		},
		authenticated
	)
}

export const DELETE = async (path, bodyOrAuthenticated = false, authenticated = false) => {
	const hasBody = typeof bodyOrAuthenticated === 'object' && bodyOrAuthenticated !== null
	const resolvedAuthenticated = hasBody ? authenticated : bodyOrAuthenticated
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'DELETE',
			...(hasBody ? { body: JSON.stringify(bodyOrAuthenticated) } : {})
		},
		resolvedAuthenticated
	)
}

// Uploads

export const PostImage = async (fileToUpload) => {
	const formData = new FormData()
	formData.append('files', fileToUpload, fileToUpload.name)
	let { headers } = await GetHeaders(true)
	delete headers['Content-Type']
	let response = await fetch(`${API_ENDPOINT}/upload`, { method: 'POST', body: formData, headers });
	let responseJson = await response.json()
	return responseJson;
}

export const PrepareImageFile = (image) => {
	let btc = window.atob(image.source)
	let btn = new Array(btc.length);
	for (var i = 0; i < btc.length; i++) {
		btn[i] = btc.charCodeAt(i);
	}
	var bta = new Uint8Array(btn);
	let boobs = new Blob([bta], { type: 'image/png' });
	return new File([boobs], `${image.filename}`)
}

export const UploadImage = (file) => {
	return new Promise((resolve, reject) => {
		let reader = new FileReader();
		reader.onload = async () => {
			let source = `${reader.result}`.split(',')[1]
			let image = {
				filename: file.name,
				filetype: file.type,
				source
			}
			let thefile = PrepareImageFile(image);
			let result = await PostImage(thefile);

			resolve(result);
		};
		reader.readAsDataURL(file);
	})
}

// Thirdy

export const ReadAddressesByZipCode = async (zipCode) => {
	try {
		let result = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
		return result.json();
	} catch (err) { return false; }
}

export const getUserIp = async () => {
	const response = await fetch('https://api.ipify.org?format=json');
	const data = await response.json();
	return data.ip;
};
